import { ref, watch, type ComputedRef, unref, onMounted, toRaw, Ref } from 'vue'
import type { BasicTableProps, PaginationProps } from '../type'
import { isFunc } from '@/utils/is'
// import type { BatchData } from './useTableBatchSelect'
import { usePageCache } from '@/hooks/usePageCache'
import { deepMerge } from '@/utils/tools'
import { getColumnWidthApi } from '@/apis/common'
import { useResource } from '@/hooks/useResource'

interface ActionType<T extends string> {
  getPaginationInfo: ComputedRef<PaginationProps>
  emit: EmitType<T>
  tableRef: Ref<Recordable>
  batchTableSelectCheck: (list: Recordable[]) => void
  clearSelection: () => void
  setPaginationInfo: (page: PaginationProps) => void
}
export function useTableData<T extends string>(
  props: ComputedRef<BasicTableProps>,
  { getPaginationInfo, emit, tableRef, batchTableSelectCheck, setPaginationInfo }: ActionType<T>
) {
  const { updateCache, getCache } = usePageCache()

  const loading = ref(false)
  // 表格数据
  const tableData = ref<Recordable[]>([])
  // 数据总数
  const total = ref(0)
  // 批量筛选返回的表格数据
  const batchSelectData = ref<Recordable[]>([])
  // 如果是传递的表格数据
  watch(
    () => unref(props).data,
    () => {
      const { data, api, isSetStage, tableTotal } = unref(props)
      if (isSetStage && data) {
        tableData.value = data
        if (tableTotal && tableTotal !== -1) {
          total.value = tableTotal
        }
      } else {
        !api && data && (tableData.value = data)
        if (!api && data) {
          if (tableTotal && tableTotal !== -1) {
            total.value = tableTotal
          }
        }
      }
    },
    {
      immediate: true
    }
  )

  async function reload(_isFirst = false) {
    // 不是第一次请求，reload要清空当前选中
    // if(!isFirst){
    //   // clearSelection()
    //   tableRef.value.scrollTo(0, 0)
    // }
    // reload 重置页脚状态
    // isInit = false
    return await fetch()
  }
  const KEY = 'vPaginationKey'
  let isInit = false
  const { getMenuAppCode } = useResource()
  async function getPagination() {
    const APPCODE = getMenuAppCode()
    const tableCode = props.value?.tableCode || APPCODE
    const { pageSize, pageNum } = unref(getPaginationInfo) as PaginationProps
    try {
      if (!isInit && unref(props).customTableConfig) {
        isInit = true
        const res = await getColumnWidthApi({ businessKey: tableCode + KEY })
        setPaginationInfo({
          pageSize: ~~(res?.[0]?.value || pageSize),
          pageNum: pageNum
        })
      }
    } catch (error) {
      console.log(error)
    }
  }
  onMounted(async () => {
    // getPagination()
    let searchInfo = undefined
    if (unref(props).immediate) {
      // usePageCache:获取缓存的searchInfo
      if (unref(props).useCache) {
        searchInfo = getCache()?.table?.searchInfo
        await fetch(searchInfo)
        initTableCache()
      } else {
        await fetch(searchInfo)
      }
    }
  })
  async function fetch(cacheSearchInfo: Recordable | undefined = undefined) {
    const {
      api,
      searchInfo,
      showPagination,
      requestHeaderConfig = '',
      showSelection,
      isDebug = false
    } = unref(props)
    // console.log(props, 'props123')
    if (!api || !isFunc(api)) return
    try {
      // 请求参数
      const params: Recordable = {
        ...deepMerge(searchInfo, cacheSearchInfo, 'replace')
      }

      if (!showPagination) {
        delete params.pageSize
        delete params.pageNum
      }
      loading.value = true
      if (showPagination) {
        await getPagination()
        const { pageSize, pageNum } = unref(getPaginationInfo) as PaginationProps
        params.pageSize = pageSize
        params.pageNum = pageNum
        // 这里要同步更新一个page
        // setPaginationInfo({
        //   pageSize: params.pageSize,
        //   pageNum: params.pageNum
        // })
      }

      // usePageCache:这里存一下请求参数
      updateCache({ table: { searchInfo: params } })
      const res = await api(params, requestHeaderConfig)
      loading.value = false
      if (showPagination) {
        tableData.value = res?.records || res?.data?.records || []
      } else {
        tableData.value = res || []
      }
      // 如果有复选框，增加选中字段，解决大数据渲染
      if (showSelection) {
        const selectList = [
          ...tableRef.value.getCheckboxRecords(),
          ...tableRef.value.getCheckboxReserveRecords(true)
        ].map(item => item.id)

        tableData.value.forEach(item => {
          item.customizeSelectedFields = isDebug && selectList.includes(item.id)
        })
      }
      // console.log(tableData, 'tableData123', '页码啊', unref(props).tableTotal)
      total.value =
        unref(props).tableTotal && unref(props).tableTotal !== -1
          ? unref(props).tableTotal
          : res?.total || res?.data?.total || 0
      emit('getTableData' as T, {
        tableData: toRaw(unref(tableData)),
        total: unref(total),
        api
      })
    } catch (err) {
      console.log(err)
      loading.value = false
    }
  }

  // 批量选择调用接口
  async function batchSelectFetch(customerReq: Recordable) {
    const { api, searchInfo, showPagination, pagination } = unref(props)
    if (!api || !isFunc(api)) return
    try {
      const { pageSize, pageNum } = unref(getPaginationInfo) as PaginationProps
      // 请求参数
      const params: Recordable = {
        ...searchInfo,
        ...customerReq
      }
      delete params.columns
      params.columnSerials = [searchInfo?.columnSerials[0]]
      if (!showPagination) {
        delete params.pageSize
        delete params.pageNum
      }
      loading.value = true
      if (showPagination) {
        params.pageSize = ref(pagination).value?.pageSize || pageSize
        params.pageNum = pageNum
      }
      const res = await api(params)
      loading.value = false
      if (showPagination) {
        batchSelectData.value = res?.records || []
      } else {
        batchSelectData.value = res || []
      }
      // total.value = res?.total || 0
      // emit('getTableData', { tableData: toRaw(unref(tableData)), total: unref(total), api })
    } catch (err) {
      console.log(err)
      loading.value = false
    }
  }

  // 初始化表格缓存（滚动位置，已选行等）
  function initTableCache() {
    if (!unref(props).useCache) return
    // 这里的延迟跟表格的渲染耗时有关
    setTimeout(() => {
      const { scrollX, scrollY, sectionList } = getCache()?.table || {}
      // TODO 排序
      // 选中 （多选 单选 全选）
      if (sectionList?.length) {
        batchTableSelectCheck(sectionList)
      }
      console.log(scrollX, scrollY, 'ggggg')
      // 更新表格行为（滚动位置）
      setTimeout(() => {
        // batchTableSelectCheck会导致scrollTo不生效，这里要加个timeout
        unref(tableRef)?.scrollTo(scrollX || 0, scrollY || 0)
      }, 1)
    }, 1000)
  }
  return {
    tableData,
    fetch,
    reload,
    total,
    loading,
    batchSelectFetch,
    batchSelectData,
    initTableCache,
    getPagination
  }
}
